import { FlattenSimpleInterpolation } from 'styled-components/macro';
import { css } from '../theme/styled';
import type { V4Theme } from '../theme';
import { ButtonMode } from './button.types';
import {
  hover,
  selected,
  selectedHover,
  focus,
  counterStyles,
} from './styled-mixins';

type ButtonColorsMap = Record<
  ButtonMode,
  Record<string, FlattenSimpleInterpolation>
>;

export const buttonColorsMap = (
  colors: V4Theme['colors']
): ButtonColorsMap => ({
  accent: {
    main: css`
      background-color: ${colors.primary[600]};
      color: ${colors.white};
      ${counterStyles({
        color: colors.primary[600],
        bg: colors.white,
      })}

      ${hover(css`
        background-color: ${colors.primary[700]};
        ${counterStyles({
          color: colors.primary[700],
        })}
      `)}

      ${focus(colors.primary[100])}
    `,
    outline: css`
      border-color: ${colors.primary[600]};
      background-color: ${colors.white};
      color: ${colors.primary[600]};
      ${counterStyles({
        color: colors.white,
        bg: colors.primary[600],
      })}

      ${hover(css`
        border-color: ${colors.primary[700]};
        color: ${colors.primary[700]};
        ${counterStyles({
          bg: colors.primary[700],
        })}
      `)}

      ${selected(css`
        background-color: ${colors.primary[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.primary[600],
          bg: colors.white,
        })}
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[700]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.primary[600],
          bg: colors.white,
        })}
      `)}

      ${focus(colors.primary[100])}
    `,
    minor: css`
      background-color: ${colors.white};
      color: ${colors.primary[600]};
      ${counterStyles({
        color: colors.white,
        bg: colors.primary[600],
      })}

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })}
      `)}

      ${selected(css`
        background-color: ${colors.primary[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.primary[600],
          bg: colors.white,
        })}
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[700]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.primary[700],
          bg: colors.white,
        })}
      `)}

      ${focus(colors.primary[100])}
    `,
    ghost: css`
      background-color: transparent;
      color: ${colors.primary[600]};
      ${counterStyles({
        color: colors.white,
        bg: colors.primary[600],
      })}

      ${hover(css`
        color: ${colors.primary[700]};
        ${counterStyles({
          bg: colors.primary[700],
        })}
      `)}

      ${selected(css`
        background-color: ${colors.primary[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.primary[600],
          bg: colors.white,
        })}
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[700]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.primary[700],
          bg: colors.white,
        })}
      `)}

      ${focus(colors.primary[100])}
    `,
  },
  neutral: {
    main: css`
      background-color: ${colors.gray[100]};
      color: ${colors.gray[800]};
      ${counterStyles({
        color: colors.gray[100],
        bg: colors.gray[800],
      })}

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })}
      `)};

      ${selected(css`
        background-color: ${colors.primary[100]};
        ${counterStyles({
          color: colors.primary[100],
        })}
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[200]};
        ${counterStyles({
          color: colors.primary[200],
        })}
      `)}

      ${focus(colors.primary[100])}
    `,
    outline: css`
      border-color: ${colors.gray[300]};
      background-color: ${colors.gray[200]};
      color: ${colors.gray[800]};
      ${counterStyles({
        color: colors.gray[200],
        bg: colors.gray[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[300]};
        ${counterStyles({
          color: colors.gray[300],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.primary[100]};
        ${counterStyles({
          color: colors.primary[100],
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[200]};
        ${counterStyles({
          color: colors.primary[200],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
    minor: css`
      border-color: ${colors.gray[300]};
      background-color: ${colors.white};
      color: ${colors.gray[800]};
      ${counterStyles({
        color: colors.white,
        bg: colors.gray[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.primary[100]};
        ${counterStyles({
          color: colors.primary[100],
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[200]};
        ${counterStyles({
          color: colors.primary[200],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
    ghost: css`
      background-color: transparent;
      color: ${colors.gray[800]};
      ${counterStyles({
        color: colors.white,
        bg: colors.gray[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.primary[100]};
        ${counterStyles({
          color: colors.primary[100],
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[200]};
        ${counterStyles({
          color: colors.primary[200],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
  },
  additional: {
    main: css`
      background-color: ${colors.primary[100]};
      color: ${colors.primary[800]};
      ${counterStyles({
        color: colors.primary[100],
        bg: colors.primary[800],
      })};

      ${hover(css`
        background-color: ${colors.primary[200]};
        ${counterStyles({
          color: colors.primary[200],
        })};
      `)};

      ${selected(css`
        background-color: ${colors.primary[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.primary[600],
          bg: colors.white,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.primary[700]};
        ${counterStyles({
          color: colors.primary[700],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
  },
  success: {
    main: css`
      background-color: ${colors.green[100]};
      color: ${colors.green[800]};
      ${counterStyles({
        color: colors.green[100],
        bg: colors.green[800],
      })};

      ${hover(css`
        background-color: ${colors.green[200]};
        ${counterStyles({
          color: colors.green[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.green[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.green[600],
          bg: colors.white,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.green[700]};
        ${counterStyles({
          color: colors.green[700],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
    ghost: css`
      background-color: transparent;
      color: ${colors.green[800]};
      ${counterStyles({
        color: colors.white,
        bg: colors.green[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.green[100]};
        ${counterStyles({
          color: colors.green[100],
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.green[200]};
        ${counterStyles({
          color: colors.green[200],
        })};
      `)}

      ${focus(colors.green[100])}
    `,
  },
  warning: {
    main: css`
      background-color: ${colors.orange[100]};
      color: ${colors.orange[800]};
      ${counterStyles({
        color: colors.orange[100],
        bg: colors.orange[800],
      })};

      ${hover(css`
        background-color: ${colors.orange[200]};
        ${counterStyles({
          color: colors.orange[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.orange[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.orange[600],
          bg: colors.white,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.orange[700]};
        ${counterStyles({
          color: colors.orange[700],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
    ghost: css`
      background-color: transparent;
      color: ${colors.orange[800]};
      ${counterStyles({
        color: colors.white,
        bg: colors.orange[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.orange[100]};
        ${counterStyles({
          color: colors.orange[100],
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.orange[200]};
        ${counterStyles({
          color: colors.orange[200],
        })};
      `)}

      ${focus(colors.orange[100])}
    `,
  },
  error: {
    main: css`
      background-color: ${colors.red[100]};
      color: ${colors.red[800]};
      ${counterStyles({
        color: colors.red[100],
        bg: colors.red[800],
      })};

      ${hover(css`
        background-color: ${colors.red[200]};
        ${counterStyles({
          color: colors.red[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.red[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.red[600],
          bg: colors.white,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.red[700]};
        ${counterStyles({
          color: colors.red[700],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
    ghost: css`
      background-color: transparent;
      color: ${colors.red[800]};
      ${counterStyles({
        color: colors.white,
        bg: colors.red[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[100],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.red[100]};
        ${counterStyles({
          color: colors.red[100],
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.red[200]};
        ${counterStyles({
          color: colors.red[200],
        })};
      `)}

      ${focus(colors.red[100])}
    `,
  },
  super: {
    main: css`
      background-color: ${colors.purple[600]};
      color: ${colors.white};
      ${counterStyles({
        color: colors.purple[600],
        bg: colors.white,
      })};

      ${hover(css`
        background-color: ${colors.purple[700]};
        ${counterStyles({
          color: colors.purple[700],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
    secondary: css`
      background-color: ${colors.purple[100]};
      color: ${colors.purple[800]};
      ${counterStyles({
        color: colors.purple[100],
        bg: colors.purple[800],
      })};

      ${hover(css`
        background-color: ${colors.purple[200]};
        ${counterStyles({
          color: colors.purple[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.purple[600]};
        color: ${colors.white};
        ${counterStyles({
          color: colors.purple[600],
          bg: colors.white,
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.purple[700]};
        ${counterStyles({
          color: colors.purple[700],
        })};
      `)}

      ${focus(colors.gray[100])}
    `,
    tertiary: css`
      background-color: ${colors.gray[100]};
      color: ${colors.purple[800]};
      ${counterStyles({
        color: colors.gray[100],
        bg: colors.purple[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })};
      `)}

      ${selected(css`
        background-color: ${colors.purple[100]};
        ${counterStyles({
          color: colors.purple[100],
        })};
      `)}

      ${selectedHover(css`
        background-color: ${colors.purple[200]};
        ${counterStyles({
          color: colors.purple[200],
        })};
      `)}

      ${focus(colors.purple[100])}
    `,
    ghost: css`
      background-color: transparent;
      color: ${colors.purple[800]};
      ${counterStyles({
        color: colors.white,
        bg: colors.purple[800],
      })};

      ${hover(css`
        background-color: ${colors.gray[200]};
        ${counterStyles({
          color: colors.gray[200],
        })};
      `)};

      ${selected(css`
        background-color: ${colors.purple[100]};
        ${counterStyles({
          color: colors.purple[100],
        })};
      `)};

      ${selectedHover(css`
        background-color: ${colors.purple[200]};
        ${counterStyles({
          color: colors.purple[200],
        })};
      `)};

      ${focus(colors.purple[100])}
    `,
  },
});
